import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { copy } from "@/content";

const MANIFESTO_IMAGE =
  "https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdyaWQlMjBkYXJrJTIwZ2xhc3MlMjBhcmNoaXRlY3R1cmFsfGVufDB8fHx8MTc5MDY5NDY3OHww&ixlib=rb-4.1.0&q=85";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const chapterColors = ["text-acid/90", "text-brand-blue", "text-brand-green"];

export const Manifesto = () => {
  const { lang } = useLanguage();
  const t = copy[lang].manifesto;
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section id="manifesto" className="border-b border-white/10" data-testid="manifesto-section">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          custom={0}
          className="mb-4 font-tty text-xs uppercase tracking-[0.3em] text-acid"
        >
          {t.kicker}
        </motion.p>
        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          custom={1}
          className="mb-20 max-w-3xl font-display text-4xl font-medium tracking-tight text-white md:text-5xl"
          data-testid="manifesto-title"
        >
          {t.title}
        </motion.h2>

        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <div ref={imgRef} className="md:sticky md:top-32">
              <div className="overflow-hidden border border-white/15">
                <motion.img
                  style={{ y }}
                  src={MANIFESTO_IMAGE}
                  alt="Portuguese architecture, dark tone"
                  className="aspect-[4/5] w-full scale-110 object-cover grayscale-[0.4]"
                  data-testid="manifesto-image"
                />
              </div>
              <p className="mt-3 font-tty text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                {t.imageCaption}
              </p>
            </div>
          </div>

          <div className="md:col-span-7">
            {t.chapters.map((chapter, i) => (
              <motion.article
                key={chapter.number}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                custom={i}
                className="group border-t border-white/10 py-12 first:border-t-0 first:pt-0"
                data-testid={`manifesto-chapter-${chapter.number}`}
              >
                <span className={`font-tty text-5xl font-light transition-colors duration-500 md:text-6xl ${chapterColors[i]}`}>
                  {chapter.number}
                </span>
                <h3 className="mt-6 font-display text-2xl font-medium italic tracking-tight text-white md:text-3xl">
                  {chapter.title}
                </h3>
                <p className="mt-4 max-w-lg font-tty text-sm leading-relaxed text-neutral-400">
                  {chapter.text}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
